import supabase from '@/api/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useSettings } from '@/hooks/useSettings';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronDown, Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

const BUCKET_NAME = 'Files';
const FOLDER_NAME = 'Image';

// Company settings form schema
const formSchema = z.object({
  companyName: z.string().min(1, 'Company name is required'),
  companyLogo: z.string().optional(),
  timeZone: z.string().min(1, 'Time zone is required'),
  timeReset: z.string().min(1, 'Time reset is required'),
  maxListHeight: z.number().min(1, 'Max list height is required'),
});

type CompanyFormValues = z.infer<typeof formSchema>;

const CompanyTab = () => {
  const { settings, updateSettings, isLoading } = useSettings();
  const [isLogoDropdownOpen, setIsLogoDropdownOpen] = useState(false);
  const [logoSearch, setLogoSearch] = useState('');
  const [images, setImages] = useState<{ name: string; url: string }[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const form = useForm<CompanyFormValues>({
    resolver: zodResolver(formSchema),
    values: {
      companyName: settings?.companyName ?? '',
      companyLogo: settings?.companyLogo ?? '',
      timeZone: settings?.timeZone ?? '',
      timeReset: settings?.timeReset ?? '',
      maxListHeight: settings?.maxListHeight ?? 300,
    },
  });

  // Reset form when settings change
  useEffect(() => {
    if (settings) {
      form.reset({
        companyName: settings.companyName ?? '',
        companyLogo: settings.companyLogo ?? '',
        timeZone: settings.timeZone ?? '',
        timeReset: settings.timeReset ?? '',
        maxListHeight: settings.maxListHeight ?? 300,
      });
    }
  }, [settings, form]);

  const fetchImages = async () => {
    try {
      const { data: files, error } = await supabase.storage.from(BUCKET_NAME).list(FOLDER_NAME, {
        limit: 100,
        offset: 0,
        sortBy: { column: 'name', order: 'asc' },
      });

      if (error) {
        console.error('Error fetching images:', error);
        return;
      }

      const imageFiles = files.filter(file => file.name.match(/\.(jpg|jpeg|png|gif|webp)$/i));

      const imageUrls = await Promise.all(
        imageFiles.map(async file => {
          const {
            data: { publicUrl },
          } = supabase.storage.from(BUCKET_NAME).getPublicUrl(`${FOLDER_NAME}/${file.name}`);

          return {
            name: file.name,
            url: publicUrl,
          };
        })
      );

      setImages(imageUrls);
    } catch (error) {
      console.error('Error processing images:', error);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsLogoDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const onSubmit = async (data: CompanyFormValues) => {
    if (!settings?.id) return;

    try {
      await updateSettings({
        id: settings.id,
        ...data,
      });
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  if (!settings || isLoading) {
    return (
      <Card className="border-none">
        <CardContent className="p-6">Loading...</CardContent>
      </Card>
    );
  }

  const filteredImages = images.filter(image =>
    image.name.toLowerCase().includes(logoSearch.toLowerCase())
  );

  const selectedImage = images.find(image => image.url === form.watch('companyLogo'));

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <Card className="border-none">
          <CardHeader>
            <CardTitle>Company Settings</CardTitle>
            <CardDescription>Manage your company profile and preferences</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="companyName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter company name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="companyLogo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Company Logo</FormLabel>
                  <FormControl>
                    <div className="relative w-full" ref={dropdownRef}>
                      <button
                        type="button"
                        onClick={() => setIsLogoDropdownOpen(!isLogoDropdownOpen)}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 border rounded-md bg-white hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {selectedImage ? (
                            <>
                              <img
                                src={selectedImage.url}
                                alt={selectedImage.name}
                                className="h-6 w-6 rounded-full object-cover flex-shrink-0"
                              />
                              <span className="truncate">{selectedImage.name}</span>
                            </>
                          ) : (
                            <span className="text-gray-500">Select logo</span>
                          )}
                        </div>
                        <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-500" />
                      </button>

                      {isLogoDropdownOpen && (
                        <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
                          <div className="p-2">
                            <div className="flex items-center gap-2 rounded-md border px-2">
                              <Search className="h-4 w-4 text-gray-500" />
                              <input
                                type="text"
                                value={logoSearch}
                                onChange={e => setLogoSearch(e.target.value)}
                                placeholder="Search logos..."
                                className="flex-1 py-2 outline-none"
                              />
                            </div>
                          </div>
                          <div
                            className={cn(
                              'max-h-[200px] overflow-auto p-1',
                              !filteredImages.length && 'p-2'
                            )}
                          >
                            {filteredImages.length > 0 ? (
                              filteredImages.map(image => (
                                <button
                                  key={image.url}
                                  type="button"
                                  onClick={() => {
                                    field.onChange(image.url);
                                    setIsLogoDropdownOpen(false);
                                    setLogoSearch('');
                                  }}
                                  className="flex w-full items-center gap-2 rounded-md px-2 py-1 hover:bg-gray-100"
                                >
                                  <img
                                    src={image.url}
                                    alt={image.name}
                                    className="h-6 w-6 rounded-full object-cover"
                                  />
                                  <span className="truncate">{image.name}</span>
                                </button>
                              ))
                            ) : (
                              <p className="text-sm text-gray-500">No logos found</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="timeZone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Time Zone</FormLabel>
                  <FormControl>
                    <select {...field} className="w-full rounded-md border px-3 py-2">
                      <option value="">Select Time Zone</option>
                      {[
                        'America/New_York',
                        'America/Chicago',
                        'America/Denver',
                        'America/Los_Angeles',
                        'America/Phoenix',
                        'America/Anchorage',
                        'America/Honolulu',
                        'America/Toronto',
                        'Europe/London',
                        'Europe/Paris',
                        'Asia/Tokyo',
                        'Asia/Shanghai',
                        'Australia/Sydney',
                        'Pacific/Auckland',
                      ].map(tz => (
                        <option key={tz} value={tz}>
                          {tz}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="timeReset"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Time Reset</FormLabel>
                  <FormControl>
                    <Input type="time" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="maxListHeight"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Max List Height (px)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      onChange={e => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <button
              type="submit"
              className="rounded-md bg-primary px-4 py-2 text-white hover:bg-primary/90"
            >
              Save Changes
            </button>
          </CardContent>
        </Card>
      </form>
    </Form>
  );
};

export default CompanyTab;
